import React, { useState, useEffect, useRef, Component } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Link,
  Plus,
  Trash2,
  Save,
  Eye,
  Settings2,
  Upload,
  Loader2,
  Calendar,
  Layers,
  Edit,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Clock,
  Image as ImageIcon,
  MoveVertical,
  ChevronRight,
  AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("NewsConfigPage Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Algo deu errado</h2>
          <p className="text-muted-foreground mb-4">
            {this.state.error?.message || "Erro inesperado"}
          </p>
          <Button onClick={() => window.location.reload()}>
            Recarregar página
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

function NewsConfigContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const iconInputRef = useRef(null);
  const itemImageRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  // Config state
  const [config, setConfig] = useState({
    links: [],
    chave_pix: "",
    pix_descricao: "Chave Pix para doações",
    cor_botao_pix: "",
    mostrar_pix: false,
    banner_url: "",
    banner_link: ""
  });

  // Programação state
  const [blocos, setBlocos] = useState([]);
  const [items, setItems] = useState([]);
  
  // Dialog states
  const [blocoDialog, setBlocoDialog] = useState(false);
  const [editingBloco, setEditingBloco] = useState(null);
  const [blocoForm, setBlocoForm] = useState({ titulo: "", descricao: "", ativo: true, formato: "lista" });
  
  const [itemDialog, setItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    titulo: "",
    descricao: "",
    data: "",
    horario: "",
    imagem_url: "",
    ativo: true,
    bloco_id: null
  });

  const [newLink, setNewLink] = useState({
    titulo: "",
    url: "",
    icone: "link",
    icone_url: "",
    cor: "#2563eb"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, blocosRes, itemsRes] = await Promise.all([
        axios.get(`${API}/news-config`),
        axios.get(`${API}/agenda/blocos`).catch(() => ({ data: [] })),
        axios.get(`${API}/agenda`).catch(() => ({ data: [] }))
      ]);
      
      // Extract banner_url from nested structure if exists
      const configData = configRes.data;
      const bannerUrl = configData.banner?.imagem_url || configData.banner_url || "";
      const bannerLink = configData.banner?.botao_link || configData.banner_link || "";
      
      setConfig(prev => ({
        ...prev,
        ...configData,
        banner_url: bannerUrl,
        banner_link: bannerLink
      }));
      setBlocos(blocosRes.data || []);
      setItems(itemsRes.data || []);
      
      // Auto-inactivate past items
      await checkAndInactivatePastItems(itemsRes.data || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Check and inactivate items with past dates
  const checkAndInactivatePastItems = async (itemsList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (const item of itemsList) {
      if (item.data && item.ativo) {
        const itemDate = new Date(item.data);
        itemDate.setHours(0, 0, 0, 0);
        
        if (itemDate < today) {
          try {
            await axios.put(`${API}/agenda/${item.id}`, { ...item, ativo: false });
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, ativo: false } : i));
          } catch (err) {
            console.error("Error auto-inactivating item:", err);
          }
        }
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert flat banner fields to nested structure for backend
      const configToSend = {
        ...config,
        banner: {
          ativo: true,
          titulo: config.banner?.titulo || "",  // Vazio se não configurado
          subtitulo: config.banner?.subtitulo || "",
          imagem_url: config.banner_url || config.banner?.imagem_url || "",
          botao_texto: config.banner?.botao_texto || "",
          botao_link: config.banner_link || config.banner?.botao_link || "",
          video_ativo: config.banner?.video_ativo || false,
          video_url: config.banner?.video_url || "",
          video_arquivo_url: config.banner?.video_arquivo_url || "",
          video_tipo: config.banner?.video_tipo || "link",
          carrossel_intervalo: config.banner?.carrossel_intervalo || 5
        }
      };
      
      // Remove flat banner fields as backend uses nested structure
      delete configToSend.banner_url;
      delete configToSend.banner_link;
      
      await axios.put(`${API}/news-config`, configToSend);
      toast.success("Configurações salvas!");
    } catch (error) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  // ========== LINKS ==========
  const handleAddLink = async () => {
    if (!newLink.titulo || !newLink.url) {
      toast.error("Preencha título e URL");
      return;
    }
    try {
      const response = await axios.post(`${API}/news-config/links`, newLink);
      setConfig(prev => ({
        ...prev,
        links: response.data.links
      }));
      setNewLink({ titulo: "", url: "", icone: "link", icone_url: "", cor: "#2563eb" });
      toast.success("Link adicionado!");
    } catch (error) {
      toast.error("Erro ao adicionar link");
    }
  };

  const handleRemoveLink = async (linkId) => {
    try {
      await axios.delete(`${API}/news-config/links/${linkId}`);
      setConfig(prev => ({
        ...prev,
        links: prev.links.filter(l => l.id !== linkId)
      }));
      toast.success("Link removido!");
    } catch (error) {
      toast.error("Erro ao remover link");
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB");
      return;
    }
    setUploadingIcon(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/news-config/link-icon/upload`, formData);
      setNewLink(prev => ({ ...prev, icone_url: res.data.icon_url, icone: "custom" }));
      toast.success("Ícone enviado!");
    } catch (error) {
      toast.error("Erro ao enviar ícone");
    } finally {
      setUploadingIcon(false);
      if (iconInputRef.current) iconInputRef.current.value = "";
    }
  };

  // ========== BANNER UPLOAD ==========
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5MB");
      return;
    }
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/news-config/banner/upload`, formData);
      setConfig(prev => ({ ...prev, banner_url: res.data.url }));
      toast.success("Banner enviado!");
    } catch (error) {
      toast.error("Erro ao enviar banner");
    } finally {
      setUploadingBanner(false);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
    }
  };

  const handleRemoveBanner = () => {
    setConfig(prev => ({ ...prev, banner_url: "" }));
    toast.success("Banner removido");
  };

  const updateLinkOrder = (index, direction) => {
    const links = [...config.links];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return;
    [links[index], links[newIndex]] = [links[newIndex], links[index]];
    links.forEach((l, i) => l.ordem = i);
    setConfig(prev => ({ ...prev, links }));
  };

  // ========== BLOCOS ==========
  const openBlocoDialog = (bloco = null) => {
    setEditingBloco(bloco);
    setBlocoForm(bloco ? { ...bloco } : { titulo: "", descricao: "", ativo: true, formato: "lista" });
    setBlocoDialog(true);
  };

  const handleSaveBloco = async () => {
    if (!blocoForm.titulo) {
      toast.error("Preencha o título do bloco");
      return;
    }
    setSaving(true);
    try {
      if (editingBloco) {
        await axios.put(`${API}/agenda/blocos/${editingBloco.id}`, blocoForm);
        setBlocos(prev => prev.map(b => b.id === editingBloco.id ? { ...b, ...blocoForm } : b));
        toast.success("Bloco atualizado!");
      } else {
        const res = await axios.post(`${API}/agenda/blocos`, blocoForm);
        setBlocos(prev => [...prev, res.data.bloco]);
        toast.success("Bloco criado!");
      }
      setBlocoDialog(false);
    } catch (error) {
      toast.error("Erro ao salvar bloco");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBloco = async (blocoId) => {
    if (!confirm("Excluir este bloco? Os itens serão movidos para Programação Geral.")) return;
    try {
      await axios.delete(`${API}/agenda/blocos/${blocoId}`);
      setBlocos(prev => prev.filter(b => b.id !== blocoId));
      // Move items to general
      setItems(prev => prev.map(i => i.bloco_id === blocoId ? { ...i, bloco_id: null } : i));
      toast.success("Bloco excluído!");
    } catch (error) {
      toast.error("Erro ao excluir bloco");
    }
  };

  const moveBlocoOrder = async (index, direction) => {
    const newBlocos = [...blocos];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newBlocos.length) return;
    [newBlocos[index], newBlocos[newIndex]] = [newBlocos[newIndex], newBlocos[index]];
    newBlocos.forEach((b, i) => b.ordem = i);
    setBlocos(newBlocos);
    
    try {
      await axios.post(`${API}/agenda/blocos/reorder`, {
        blocos: newBlocos.map((b, i) => ({ id: b.id, ordem: i }))
      });
    } catch (error) {
      console.error("Error reordering:", error);
    }
  };

  // ========== ITEMS ==========
  const openItemDialog = (item = null, blocoId = null) => {
    try {
      setEditingItem(item);
      const defaultForm = {
        titulo: "",
        descricao: "",
        data: "",
        horario: "",
        imagem_url: "",
        ativo: true,
        bloco_id: blocoId
      };
      
      if (item) {
        setItemForm({
          ...defaultForm,
          ...item,
          titulo: item.titulo || "",
          descricao: item.descricao || "",
          data: item.data || "",
          horario: item.horario || "",
          imagem_url: item.imagem_url || "",
          ativo: item.ativo !== false,
          bloco_id: item.bloco_id || blocoId
        });
      } else {
        setItemForm(defaultForm);
      }
      setItemDialog(true);
    } catch (err) {
      console.error("Error opening item dialog:", err);
      toast.error("Erro ao abrir formulário");
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.titulo) {
      toast.error("Preencha o título");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...itemForm, tipo: "programacao" };
      if (editingItem) {
        await axios.put(`${API}/agenda/${editingItem.id}`, payload);
        setItems(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...payload } : i));
        toast.success("Item atualizado!");
      } else {
        const res = await axios.post(`${API}/agenda`, payload);
        setItems(prev => [...prev, res.data.item]);
        toast.success("Item criado!");
      }
      setItemDialog(false);
    } catch (error) {
      toast.error("Erro ao salvar item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("Excluir este item?")) return;
    try {
      await axios.delete(`${API}/agenda/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Item excluído!");
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleMoveItem = async (itemId, newBlocoId) => {
    try {
      const item = items.find(i => i.id === itemId);
      if (!item) return;
      
      const updatedItem = { ...item, bloco_id: newBlocoId === "geral" ? null : newBlocoId };
      await axios.put(`${API}/agenda/${itemId}`, updatedItem);
      setItems(prev => prev.map(i => i.id === itemId ? updatedItem : i));
      toast.success("Item movido!");
    } catch (error) {
      toast.error("Erro ao mover item");
    }
  };

  const handleItemImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5MB");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/agenda/upload-image`, formData);
      setItemForm(prev => ({ ...prev, imagem_url: res.data.image_url }));
      toast.success("Imagem enviada!");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploading(false);
      if (itemImageRef.current) itemImageRef.current.value = "";
    }
  };

  // Helpers
  const getItemsForBloco = (blocoId) => {
    return items.filter(i => (i.tipo === "programacao" || !i.tipo) && i.bloco_id === blocoId)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  };

  const getUnassignedItems = () => {
    return items.filter(i => (i.tipo === "programacao" || !i.tipo) && !i.bloco_id)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
    } catch {
      return dateStr;
    }
  };

  const isPastDate = (dateStr) => {
    if (!dateStr) return false;
    const itemDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    itemDate.setHours(0, 0, 0, 0);
    return itemDate < today;
  };

  // Item Row Component
  const ItemRow = ({ item, blocoId }) => (
    <div className={`flex items-center gap-2 p-2 rounded border ${!item.ativo ? 'opacity-50 bg-gray-50' : 'bg-white'} ${isPastDate(item.data) ? 'border-amber-300' : ''}`}>
      {item.imagem_url && (
        <img src={item.imagem_url} alt="" className="w-10 h-10 rounded object-cover" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{item.titulo}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {item.data && (
            <span className={isPastDate(item.data) ? 'text-amber-600' : ''}>
              {formatDate(item.data)}
            </span>
          )}
          {item.horario && <span>{item.horario}</span>}
          {!item.ativo && <Badge variant="secondary" className="text-xs">Inativo</Badge>}
          {isPastDate(item.data) && item.ativo && <Badge variant="outline" className="text-xs text-amber-600">Data passada</Badge>}
        </div>
      </div>
      <Select value={item.bloco_id || "geral"} onValueChange={(val) => handleMoveItem(item.id, val)}>
        <SelectTrigger className="w-32 h-8 text-xs">
          <MoveVertical className="h-3 w-3 mr-1" />
          <SelectValue placeholder="Mover" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="geral">Geral</SelectItem>
          {blocos.map(b => (
            <SelectItem key={b.id} value={b.id}>{b.titulo}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openItemDialog(item, blocoId)}>
        <Edit className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => handleDeleteItem(item.id)}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6" />
            Configuração do News
          </h1>
          <p className="text-muted-foreground">
            Configure links e programação da página pública
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open('/news', '_blank')}>
            <Eye className="h-4 w-4 mr-2" />
            Visualizar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="banner" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="banner" data-testid="tab-banner">
            <ImageIcon className="h-4 w-4 mr-2" />
            Banner
          </TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links">
            <Link className="h-4 w-4 mr-2" />
            Links
          </TabsTrigger>
          <TabsTrigger value="programacao" data-testid="tab-programacao">
            <Calendar className="h-4 w-4 mr-2" />
            Programação
          </TabsTrigger>
          <TabsTrigger value="pix" data-testid="tab-pix">
            <span className="mr-2">💰</span>
            Pix
          </TabsTrigger>
        </TabsList>

        {/* ========== BANNER TAB ========== */}
        <TabsContent value="banner" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Banner Principal
              </CardTitle>
              <CardDescription>
                Configure o banner que aparece no topo da página News
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preview e Upload */}
              <div className="space-y-4">
                <Label>Imagem do Banner</Label>
                {config.banner_url ? (
                  <div className="relative rounded-lg overflow-hidden border">
                    <img 
                      src={config.banner_url} 
                      alt="Banner Preview" 
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8"
                        onClick={() => bannerInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="destructive"
                        className="h-8 w-8"
                        onClick={handleRemoveBanner}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => bannerInputRef.current?.click()}
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    {uploadingBanner ? (
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Clique para fazer upload do banner</p>
                        <p className="text-xs text-muted-foreground mt-1">Recomendado: 1200x400px • Máx 5MB</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleBannerUpload}
                />
              </div>

              <Separator />

              {/* Título do Banner */}
              <div className="space-y-2">
                <Label>Título do Banner (opcional)</Label>
                <Input
                  value={config.banner?.titulo || ""}
                  onChange={(e) => setConfig(prev => ({ 
                    ...prev, 
                    banner: { ...prev.banner, titulo: e.target.value }
                  }))}
                  placeholder="Ex: Bem-vindo à nossa igreja"
                />
                <p className="text-xs text-muted-foreground">
                  Texto que aparece sobre o banner. Deixe vazio para não exibir texto.
                </p>
              </div>

              {/* Link do Banner */}
              <div className="space-y-2">
                <Label>Link ao clicar no banner (opcional)</Label>
                <Input
                  value={config.banner_link || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, banner_link: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-muted-foreground">
                  Se preenchido, ao clicar no banner o usuário será redirecionado para este link
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== LINKS TAB ========== */}
        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Links de Atalho</CardTitle>
              <CardDescription>
                Configure os links estilo Instagram que aparecem na página News
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new link form */}
              <div className="p-4 rounded-lg bg-muted/50 space-y-4">
                <h4 className="font-medium">Adicionar novo link</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Título</Label>
                    <Input
                      value={newLink.titulo}
                      onChange={(e) => setNewLink(prev => ({ ...prev, titulo: e.target.value }))}
                      placeholder="Ex: Instagram"
                      data-testid="new-link-titulo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL</Label>
                    <Input
                      value={newLink.url}
                      onChange={(e) => setNewLink(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://..."
                      data-testid="new-link-url"
                    />
                  </div>
                </div>
                <div className="flex gap-4 items-end flex-wrap">
                  <div className="space-y-2">
                    <Label>Cor</Label>
                    <Input
                      type="color"
                      value={newLink.cor}
                      onChange={(e) => setNewLink(prev => ({ ...prev, cor: e.target.value }))}
                      className="h-10 w-20"
                      data-testid="new-link-cor"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ícone Personalizado</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={iconInputRef}
                        accept="image/*"
                        onChange={handleIconUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => iconInputRef.current?.click()}
                        disabled={uploadingIcon}
                      >
                        {uploadingIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                        {uploadingIcon ? "..." : "Upload"}
                      </Button>
                      {newLink.icone_url && (
                        <div className="flex items-center gap-2">
                          <img src={newLink.icone_url} alt="Ícone" className="w-8 h-8 rounded object-cover" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setNewLink(prev => ({ ...prev, icone_url: "", icone: "link" }))}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button onClick={handleAddLink} data-testid="add-link-btn" className="ml-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Links list */}
              <div className="space-y-2">
                <h4 className="font-medium">Links configurados ({config.links?.length || 0})</h4>
                {!config.links?.length ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">
                    Nenhum link configurado
                  </p>
                ) : (
                  config.links
                    .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                    .map((link, index) => (
                      <div key={link.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateLinkOrder(index, 'up')} disabled={index === 0}>
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateLinkOrder(index, 'down')} disabled={index === config.links.length - 1}>
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>
                        {link.icone_url ? (
                          <img src={link.icone_url} alt={link.titulo} className="w-8 h-8 rounded-lg object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: link.cor || "#2563eb" }}>
                            <Link className="h-4 w-4 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{link.titulo}</p>
                          <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveLink(link.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== PROGRAMAÇÃO TAB ========== */}
        <TabsContent value="programacao" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Blocos de Programação
                  </CardTitle>
                  <CardDescription>
                    Organize a programação em blocos. Itens com data passada são inativados automaticamente.
                  </CardDescription>
                </div>
                <Button onClick={() => openBlocoDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Bloco
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {blocos.length === 0 && getUnassignedItems().length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Layers className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Nenhum bloco de programação</p>
                  <p className="text-sm mb-4">Crie blocos para organizar sua programação</p>
                  <Button variant="outline" onClick={() => openBlocoDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar primeiro bloco
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bloco Geral (itens sem bloco) */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">Programação Geral</span>
                        <Badge variant="secondary" className="text-xs">{getUnassignedItems().length}</Badge>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => openItemDialog(null, null)}>
                        <Plus className="h-4 w-4 mr-1" /> Adicionar
                      </Button>
                    </div>
                    {getUnassignedItems().length > 0 ? (
                      <div className="p-2 space-y-1">
                        {getUnassignedItems().map((item) => (
                          <ItemRow key={item.id} item={item} blocoId={null} />
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Nenhum item sem bloco
                      </div>
                    )}
                  </div>

                  {/* Blocos criados */}
                  {blocos.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).map((bloco, index) => (
                    <div key={bloco.id} className={`border rounded-lg overflow-hidden ${!bloco.ativo ? 'opacity-60' : ''}`}>
                      <div className="bg-primary/5 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveBlocoOrder(index, 'up')} disabled={index === 0}>
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => moveBlocoOrder(index, 'down')} disabled={index === blocos.length - 1}>
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>
                          <span className="font-medium">{bloco.titulo}</span>
                          <Badge variant="outline" className="text-xs">{getItemsForBloco(bloco.id).length}</Badge>
                          {!bloco.ativo && <Badge variant="secondary">Inativo</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openItemDialog(null, bloco.id)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openBlocoDialog(bloco)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteBloco(bloco.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {getItemsForBloco(bloco.id).length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Nenhum item neste bloco
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {getItemsForBloco(bloco.id).map((item) => (
                            <ItemRow key={item.id} item={item} blocoId={bloco.id} />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== PIX TAB ========== */}
        <TabsContent value="pix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuração do Pix</CardTitle>
              <CardDescription>
                Configure a chave Pix para doações
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Mostrar botão Pix</Label>
                <Switch
                  checked={config.mostrar_pix}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, mostrar_pix: checked }))}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Chave Pix</Label>
                <Input
                  value={config.chave_pix || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, chave_pix: e.target.value }))}
                  placeholder="email@exemplo.com ou CPF/CNPJ"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição do Pix</Label>
                <Input
                  value={config.pix_descricao || ""}
                  onChange={(e) => setConfig(prev => ({ ...prev, pix_descricao: e.target.value }))}
                  placeholder="Pix CNPJ | Nome da Igreja"
                />
              </div>
              <div className="space-y-2">
                <Label>Cor do botão</Label>
                <Input
                  type="color"
                  value={config.cor_botao_pix || "#b45309"}
                  onChange={(e) => setConfig(prev => ({ ...prev, cor_botao_pix: e.target.value }))}
                  className="h-10 w-24"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ========== DIALOG: BLOCO ========== */}
      <Dialog open={blocoDialog} onOpenChange={setBlocoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBloco ? "Editar Bloco" : "Novo Bloco"}</DialogTitle>
            <DialogDescription>Configure as informações do bloco de programação</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={blocoForm.titulo}
                onChange={(e) => setBlocoForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ex: Cultos da Semana"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={blocoForm.descricao || ""}
                onChange={(e) => setBlocoForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descrição opcional"
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Bloco ativo</Label>
              <Switch
                checked={blocoForm.ativo !== false}
                onCheckedChange={(v) => setBlocoForm(prev => ({ ...prev, ativo: v }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlocoDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveBloco} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingBloco ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: ITEM ========== */}
      <Dialog open={itemDialog} onOpenChange={(open) => {
        if (!open) {
          setItemDialog(false);
          setEditingItem(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Item" : "Novo Item"}</DialogTitle>
            <DialogDescription>Configure as informações do item da programação</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 py-4 pr-4">
              <div>
                <Label>Título *</Label>
                <Input
                  value={itemForm.titulo || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setItemForm(prev => ({ ...prev, titulo: value }));
                  }}
                  placeholder="Ex: Culto de Domingo"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={itemForm.descricao || ""}
                  onChange={(e) => setItemForm(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição do evento"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={itemForm.data || ""}
                    onChange={(e) => setItemForm(prev => ({ ...prev, data: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Horário</Label>
                  <Input
                    type="time"
                    value={itemForm.horario || ""}
                    onChange={(e) => setItemForm(prev => ({ ...prev, horario: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Bloco</Label>
                <Select value={itemForm.bloco_id || "geral"} onValueChange={(val) => setItemForm(prev => ({ ...prev, bloco_id: val === "geral" ? null : val }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o bloco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geral">Programação Geral</SelectItem>
                    {blocos.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Imagem</Label>
                {itemForm.imagem_url ? (
                  <div className="relative mt-1 rounded-lg overflow-hidden">
                    <img src={itemForm.imagem_url} alt="" className="w-full h-32 object-cover" />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2 h-7 w-7"
                      onClick={() => setItemForm(prev => ({ ...prev, imagem_url: "" }))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => itemImageRef.current?.click()}
                    className="mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50"
                  >
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    ) : (
                      <>
                        <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                        <p className="text-sm text-muted-foreground">Clique para enviar</p>
                      </>
                    )}
                  </div>
                )}
                <input ref={itemImageRef} type="file" accept="image/*" onChange={handleItemImageUpload} className="hidden" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Item ativo</Label>
                <Switch
                  checked={itemForm.ativo !== false}
                  onCheckedChange={(v) => setItemForm(prev => ({ ...prev, ativo: v }))}
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveItem} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingItem ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function NewsConfigPage() {
  return (
    <ErrorBoundary>
      <NewsConfigContent />
    </ErrorBoundary>
  );
}
